import type { GeneratorSizingResponse } from "../services/generatorSizing";
import {
  NOT_ESTABLISHED_WARNING_CODES,
  type GeneratorRedundancyMode,
} from "../services/generatorSizingContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  REDUNDANCY_LABELS as TRANSFORMER_REDUNDANCY_LABELS,
  STATUS_LABELS as TRANSFORMER_STATUS_LABELS,
  STATUS_TONE as TRANSFORMER_STATUS_TONE,
} from "./TransformerResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The same words and the same colour as the Transformer summary: an
// unestablished input is a review the engineer owes, not a design failure.
// No adequate rating is a failure - there is nothing to build with. The maps
// are the Transformer's, typed here against the generator enums so a value
// only the generator gains cannot pass without words and a colour.
const STATUS_LABELS: Record<GeneratorSizingResponse["status"], string> =
  TRANSFORMER_STATUS_LABELS;

const STATUS_TONE: Record<GeneratorSizingResponse["status"], "pass" | "fail" | "warning"> =
  TRANSFORMER_STATUS_TONE;

const REDUNDANCY_LABELS: Record<GeneratorRedundancyMode, string> = TRANSFORMER_REDUNDANCY_LABELS;

type GoverningBasis = "steady-state" | "transient";

// The engine names the governing requirement itself: it raises
// TRANSIENT_REQUIREMENT_GOVERNS exactly when the transient requirement is the
// larger one, so the basis is read from the result rather than recomputed here.
function governingBasis(result: GeneratorSizingResponse): GoverningBasis {
  return result.warnings.some((warning) => warning.code === "TRANSIENT_REQUIREMENT_GOVERNS")
    ? "transient"
    : "steady-state";
}

type GeneratorResultSummaryProps = {
  result: GeneratorSizingResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): sizing state, the
// rating chosen from the schedule, the requirement that governed it and the unit
// rating it needed after derating.
// Display rule A12: four significant figures on screen, the exact engine
// decimal in the tooltip (title attribute). The selected rating is a catalogue
// value the engineer supplied, so it is shown exactly as it was sent.
// The loading figure is left out on purpose: it is the steady-state design
// requirement (growth and margin included) over the derated capacity, and only
// the result panel shows it, under a label that says exactly that.
export function GeneratorResultSummary({ result }: GeneratorResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const warningCount = result.warnings.length;
  const notEstablishedCount = result.warnings.filter((warning) =>
    (NOT_ESTABLISHED_WARNING_CODES as readonly string[]).includes(warning.code),
  ).length;
  const basis = governingBasis(result);

  const governing = formatQuantity(result.governing_required_kva, "kVA");
  const requiredRating = formatQuantity(result.required_unit_rating_kva, "kVA");

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {STATUS_LABELS[result.status]}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} ·{" "}
          {result.jurisdiction_profile} profile
        </p>
        {result.selected_unit_rating_kva === null ? (
          <p data-no-rating="true">
            No rating in the schedule covers {requiredRating.display}.
          </p>
        ) : null}
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Selected unit rating</dt>
          {/* A catalogue value from the project design basis: shown as sent, so
              the engineer reads back the rating that is actually orderable. */}
          <dd data-summary-field="selected-rating" data-summary-value="text">
            {result.selected_unit_rating_kva === null
              ? EMPTY_QUANTITY
              : `${result.duty_units} × ${result.selected_unit_rating_kva} kVA`}
          </dd>
        </div>
        <div>
          <dt>Governing requirement</dt>
          <dd data-summary-field="governing-requirement" data-summary-value="text" title={governing.exact ?? undefined}>
            {governing.display} <span data-governing-basis={basis}>({basis})</span>
          </dd>
        </div>
        <div>
          <dt>Required unit rating (after derating)</dt>
          <dd data-summary-field="required-unit-rating" title={requiredRating.exact ?? undefined}>
            {requiredRating.display}
          </dd>
        </div>
        <div>
          <dt>Units</dt>
          {/* Counts, not engineering quantities: shown as the integers they are. */}
          <dd data-summary-field="units" data-summary-value="text">
            {result.duty_units} duty + {result.standby_units} standby ·{" "}
            {REDUNDANCY_LABELS[result.redundancy_mode]}
          </dd>
        </div>
        {notEstablishedCount > 0 ? (
          <div>
            <dt>Inputs not established</dt>
            <dd data-summary-field="not-established">{notEstablishedCount}</dd>
          </div>
        ) : null}
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { REDUNDANCY_LABELS, STATUS_LABELS, STATUS_TONE };
